import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Badge,
  Spinner,
  Progress,
} from '@chakra-ui/react';
import { DialogRoot, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogBackdrop } from '../components/ui/dialog';
import { FiChevronLeft, FiChevronRight, FiCheck, FiDownload } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import step components
import PolicyUploadStep from '../components/CreateReview/PolicyUploadStep';
import StandardSelectionStep from '../components/CreateReview/StandardSelectionStep';
import TemplateSelectionStep from '../components/CreateReview/TemplateSelectionStep';
import EvidenceUploadStep from '../components/CreateReview/EvidenceUploadStep';
import ReviewSummaryStep from '../components/CreateReview/ReviewSummaryStep';

// Import hooks and types
import { useDownloadReviewReport } from '../hooks/useApi';
import { Policy, SystemStandard, ReviewTemplate, Evidence } from '../types';
import { toaster } from '../components/ui/toaster';
import { getAssessmentUnitLabels } from '../utils/frameworkTerminology';

// Step configuration
const STEP_CONFIG = [
  {
    id: 'standard-selection',
    titleKey: 'selectFramework',
    descriptionKey: 'chooseComplianceFramework',
    icon: '🎯',
  },
  {
    id: 'policy-upload',
    titleKey: 'uploadDocument',
    descriptionKey: 'uploadYourPolicyDocument',
    icon: '📄',
  },
  {
    id: 'template-selection',
    titleKey: 'assessmentScope',
    descriptionKey: 'selectSectionsToAssess',
    icon: '📋',
  },
  {
    id: 'evidence-upload',
    titleKey: 'uploadEvidence',
    descriptionKey: 'uploadSupportingEvidence',
    icon: '📎',
  },
  {
    id: 'summary',
    titleKey: 'reviewAndSubmit',
    descriptionKey: 'finalReviewAndSubmission',
    icon: '✅',
  },
];

export interface CreateReviewData {
  // Step 1: Standard/Framework Selection
  selectedStandard: SystemStandard | null;

  // Step 2: Policy Upload
  policy: Policy | null;
  
  // Step 3: Template Selection
  selectedTemplate: ReviewTemplate | null;
  customSections: string[];
  selectedSections: string[];
  selectedSubsections: {[key: string]: string[]};  // Track subsection selections
  
  // Step 4: Evidence Upload
  evidenceFiles: Evidence[];
  aiLinkedEvidence: Array<{
    sectionId: string;
    evidenceIds: string[];
  }>;
  manualEvidenceLinks: Array<{
    sectionId: string;
    evidenceIds: string[];
    typeKey?: string;
  }>;
  
  // Step 5: Summary
  estimatedPrice: number;
  reviewName: string;
  reviewDescription: string;
  language: string;
}

const CreateReviewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const [currentStep, setCurrentStep] = useState(0);
  const [reviewData, setReviewData] = useState<CreateReviewData>({
    policy: null,
    selectedStandard: null,
    selectedTemplate: null,
    customSections: [],
    selectedSections: [],
    selectedSubsections: {},
    evidenceFiles: [],
    aiLinkedEvidence: [],
    manualEvidenceLinks: [],
    estimatedPrice: 0,
    reviewName: '',
    reviewDescription: '',
    language: i18n.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepValidation, setStepValidation] = useState<Record<number, boolean>>({});
  const [mode, setMode] = useState<'form' | 'progress'>('form');
  const [createdReviewId, setCreatedReviewId] = useState<number | null>(null);
  const [reviewStatus, setReviewStatus] = useState<any>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const assessmentTerminology = getAssessmentUnitLabels(
    reviewData.selectedStandard?.standard_type,
    reviewData.selectedSections,
    i18n.language
  );
  const progressUnitLabel = reviewData.selectedStandard?.standard_type
    ? getAssessmentUnitLabels(reviewData.selectedStandard.standard_type, reviewData.selectedSections, i18n.language)
    : assessmentTerminology;

  const steps = STEP_CONFIG.map((step) => ({
    ...step,
    title: t(`createReview.${step.titleKey}`),
    description: step.descriptionKey === 'selectSectionsToAssess'
      ? t(`createReview.${step.descriptionKey}`, { unitPlural: assessmentTerminology.plural })
      : t(`createReview.${step.descriptionKey}`),
  }));

  // Hooks
  const downloadMutation = useDownloadReviewReport();

  const handleStopAssessment = async () => {
    if (!createdReviewId) return;
    setIsStopping(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/reviews/${createdReviewId}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('createReview.stopAssessmentFailed'));
      }
      toaster.create({ title: t('createReview.assessmentStopRequested'), type: 'info' });
      setShowStopConfirm(false);
    } catch (e: any) {
      toaster.create({ title: e.message || t('createReview.stopAssessmentFailed'), type: 'error' });
    } finally {
      setIsStopping(false);
    }
  };

  // Validation functions for each step
  const validateStep = useCallback((step: number): boolean => {
    switch (step) {
      case 0: // Standard/Framework Selection
        return !!reviewData.selectedStandard;
      case 1: // Policy Upload
        return !!reviewData.policy;
      case 2: // Template Selection
        return reviewData.selectedSections.length > 0;
      case 3: // Evidence Upload
        return true; // Evidence is optional
      case 4: // Summary
        return !!reviewData.reviewName.trim();
      default:
        return false;
    }
  }, [reviewData]);

  // Update step validation when data changes
  React.useEffect(() => {
    const isValid = validateStep(currentStep);
    setStepValidation(prev => ({ ...prev, [currentStep]: isValid }));
  }, [currentStep, reviewData, validateStep]);

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toaster.create({
        title: t('createReview.validationError'),
        description: t('createReview.completeRequiredFields'),
        type: 'error',
        duration: 3000,
      });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Allow navigation to completed steps or current step
    if (stepIndex <= currentStep || stepValidation[stepIndex]) {
      setCurrentStep(stepIndex);
    }
  };

  const handleDataUpdate = (updates: Partial<CreateReviewData>) => {
    setReviewData(prev => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    if (!reviewData.reviewName.trim()) {
      toaster.create({
        title: t('createReview.assessmentNameRequired'),
        description: t('createReview.enterAssessmentNameBeforeSubmitting'),
        type: 'warning',
        duration: 4000,
      });
      return;
    }
    setIsSubmitting(true);
    setShowLoadingModal(true);
    setShowErrorModal(false);
    setErrorMessage('');
    try {
      // Build payload for /api/reviews
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = {
        policy_id: reviewData.policy?.id,
        system_standard_id: reviewData.selectedStandard?.id,
        selected_sections: reviewData.selectedSections,
        selected_subsections: reviewData.selectedSubsections,
        custom_sections: reviewData.customSections,
        template_id: reviewData.selectedTemplate?.id,
        review_name: reviewData.reviewName,
        review_description: reviewData.reviewDescription,
        evidence_files: reviewData.evidenceFiles.map(e => e.id), // Add this line
        ai_linked_evidence: reviewData.aiLinkedEvidence, // Optionally include AI links
        manual_evidence_links: reviewData.manualEvidenceLinks, // Optionally include manual links
        user_timezone: userTimezone,
        language: reviewData.language,
      };

      // Get auth token (HttpOnly cookie is the primary mechanism; bearer is a fallback)
      const token = localStorage.getItem('access_token');

      // POST to /api/reviews
      const response = await fetch('/api/reviews', {
        method: 'POST',
        credentials: 'include', // send HttpOnly auth cookie (matches services/api.ts)
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMsg = t('createReview.failedToCreateReview');
        if (errorBody.detail) {
          if (typeof errorBody.detail === 'string') {
            errorMsg = errorBody.detail;
          } else if (Array.isArray(errorBody.detail)) {
            errorMsg = errorBody.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else if (typeof errorBody.detail === 'object' && errorBody.detail.error) {
            errorMsg = errorBody.detail.reason || errorBody.detail.error;
          }
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      const reviewId = result.id || result.review_id;

      setShowLoadingModal(false);
      setShowSuccessModal(false);
      setMode('progress');
      setCreatedReviewId(reviewId);
      toaster.create({
        title: t('createReview.assessmentCreated'),
        description: t('createReview.assessmentStartedTrackProgress'),
        type: 'success',
        duration: 2000,
      });
    } catch (error: any) {
      setShowLoadingModal(false);
      setShowErrorModal(true);
      setErrorMessage(error.message || t('createReview.failedToCreateAssessmentTryAgain'));
      toaster.create({
        title: t('createReview.error'),
        description: error.message || t('createReview.failedToCreateAssessmentTryAgain'),
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Poll review status in progress mode
  useEffect(() => {
    if (mode === 'progress' && createdReviewId) {
      let errorCount = 0;
      const pollStartedAt = Date.now();
      const MAX_POLL_MS = 30 * 60 * 1000; // hard stop after 30 minutes
      const fetchStatus = async () => {
        // Safety valve: stop polling if a review never reaches a terminal state
        // (e.g. a worker died mid-processing) so the UI doesn't spin forever.
        if (Date.now() - pollStartedAt > MAX_POLL_MS) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setReviewStatus((prev: any) => ({ ...(prev || {}), status: prev?.status || 'error', timed_out: true }));
          return;
        }
        try {
          const token = localStorage.getItem('access_token');
          const response = await fetch(`/api/reviews/${createdReviewId}/status`, {
            credentials: 'include', // send HttpOnly auth cookie (matches services/api.ts)
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          });
          if (response.ok) {
            errorCount = 0;
            const statusData = await response.json();
            setReviewStatus(statusData);
            // Stop polling once review reaches terminal state
            if (statusData.status === 'completed' || statusData.status === 'completed_with_errors' || statusData.status === 'failed' || statusData.status === 'error') {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
            }
          } else {
            // Stop polling after repeated failures (e.g. auth expired)
            errorCount++;
            if (errorCount >= 5 && pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }
        } catch (e) {
          // Stop polling after repeated network errors
          errorCount++;
          if (errorCount >= 5 && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      };
      fetchStatus();
      pollingRef.current = setInterval(fetchStatus, 2000);
      return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [mode, createdReviewId]);

  const handleCancel = () => {
    setIsOpen(true);
  };

  const confirmCancel = () => {
    setIsOpen(false);
    navigate('/dashboard');
  };

  const handleDownload = async () => {
    if (!createdReviewId) return;
    
    try {
      const blob = await downloadMutation.mutateAsync(createdReviewId.toString());
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const policyName = reviewData.policy?.tag?.replace(/[^a-zA-Z0-9]/g, '_') || 'policy';
      const standardName = reviewData.selectedStandard?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'standard';
      link.download = `NEUREV_Assessment_${policyName}_${standardName}_${createdReviewId}.docx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toaster.create({
        title: t('createReview.reportDownloadedSuccessfully'),
        type: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      let description = t('createReview.errorOccurred');
      const errorData = error?.response?.data;
      if (errorData instanceof Blob) {
        try {
          const parsed = JSON.parse(await errorData.text());
          description = parsed?.detail || parsed?.message || description;
        } catch {
          description = error?.message || description;
        }
      } else {
        description = errorData?.message || errorData?.detail || error?.message || description;
      }
      toaster.create({
        title: t('createReview.downloadFailed'),
        description,
        type: 'error',
        duration: 5000,
      });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <StandardSelectionStep
            selectedStandard={reviewData.selectedStandard}
            onStandardSelect={(standard) => handleDataUpdate({ selectedStandard: standard })}
          />
        );
      case 1:
        return (
          <PolicyUploadStep
            policy={reviewData.policy}
            onPolicySelect={(policy) => handleDataUpdate({ policy })}
          />
        );
      case 2:
        return (
          <TemplateSelectionStep
            selectedStandard={reviewData.selectedStandard}
            selectedTemplate={reviewData.selectedTemplate}
            selectedSections={reviewData.selectedSections}
            selectedSubsections={reviewData.selectedSubsections}
            customSections={reviewData.customSections}
            onTemplateSelect={(template) => handleDataUpdate({ selectedTemplate: template })}
            onSectionsSelect={(sections) => handleDataUpdate({ selectedSections: sections })}
            onSubsectionsSelect={(subsections) => handleDataUpdate({ selectedSubsections: subsections })}
            onCustomSectionsUpdate={(sections) => handleDataUpdate({ customSections: sections })}
          />
        );
      case 3:
        return (
          <EvidenceUploadStep
            selectedSections={reviewData.selectedSections}
            selectedSubsections={reviewData.selectedSubsections}
            selectedStandard={reviewData.selectedStandard}
            evidenceFiles={reviewData.evidenceFiles}
            aiLinkedEvidence={reviewData.aiLinkedEvidence}
            manualEvidenceLinks={reviewData.manualEvidenceLinks}
            onEvidenceUpdate={(evidence) => handleDataUpdate({ evidenceFiles: evidence })}
            onAiLinksUpdate={(links) => handleDataUpdate({ aiLinkedEvidence: links })}
            onManualLinksUpdate={(links) => handleDataUpdate({ manualEvidenceLinks: links })}
          />
        );
      case 4:
        return (
          <ReviewSummaryStep
            reviewData={reviewData}
            onReviewDataUpdate={handleDataUpdate}
          />
        );
      default:
        return null;
    }
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'pending';
  };

  if (mode === 'progress' && createdReviewId) {
    const total = reviewStatus?.total_sections || 0;
    const completed = reviewStatus?.completed_sections || 0;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    const isDone = reviewStatus?.status === 'completed' || reviewStatus?.status === 'completed_with_errors';
    const isFailed = reviewStatus?.status === 'failed' || reviewStatus?.status === 'error';
    const unitLabel = total === 1 ? progressUnitLabel.singular : progressUnitLabel.plural;
    
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="2xl">
          <VStack gap={8} align="stretch">
            <Box textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {t('createReview.assessmentProgress')}
              </Text>
              
              {isFailed ? (
                <VStack gap={4} mt={6}>
                  <Text color="red.500" fontSize="lg" fontWeight="medium">
                    ❌ {t('createReview.reviewFailed')}
                  </Text>
                  <Text color="gray.600" maxW="md" mx="auto">
                    {t('createReview.reviewProcessingErrorIntro')}
                  </Text>
                  <Box as="ul" textAlign="left" color="gray.600" pl={4} css={{ "& li": { mb: 1 } }}>
                    <li>• {t('createReview.aiServiceUnavailable')}</li>
                    <li>• {t('createReview.networkConnectivityIssues')}</li>
                    <li>• {t('createReview.documentProcessingErrors')}</li>
                  </Box>
                  <Text color="gray.600" fontSize="sm" mt={2}>
                    {t('createReview.errorDetails', { error: reviewStatus?.results?.error || t('createReview.unknownErrorOccurred') })}
                  </Text>
                  <VStack mt={6} gap={4}>
                    <Button 
                      colorScheme="blue" 
                      onClick={() => {
                        setMode('form');
                        setCreatedReviewId(null);
                        setReviewStatus(null);
                      }}
                    >
                      {t('createReview.tryAgain')}
                    </Button>
                    <Button {...{as: RouterLink, to: "/reviews"} as any} variant="outline" colorScheme="gray">
                      {t('createReview.goToReviewsList')}
                    </Button>
                  </VStack>
                </VStack>
              ) : (
                <>
                  <Text color="gray.600" mb={4}>
                    {t('createReview.reviewProcessingMayTakeMinutes')}
                  </Text>
                  <Progress.Root value={percent} size="lg" colorScheme={isDone ? 'green' : 'blue'} borderRadius="md" />
                  <Text mt={2} fontSize="md">
                    {t('createReview.sectionsCompleted', { completed, total, percent, unitLabel })}
                  </Text>
                  {!isDone && (
                    <VStack gap={4} mt={4}>
                      <Spinner size="lg" color="blue.500" />
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => setShowStopConfirm(true)}
                      >
                        {t('createReview.stopAssessment')}
                      </Button>
                    </VStack>
                  )}
                  {isDone && (
                    <VStack mt={6} gap={4}>
                      <Button 
                        colorScheme="green" 
                        onClick={handleDownload}
                        loading={downloadMutation.isLoading}
                      >
                        <FiDownload /> {t('createReview.downloadAssessmentReport')}
                      </Button>
                      <Button {...{as: RouterLink, to: "/reviews"} as any} variant="outline" colorScheme="blue">
                        {t('createReview.goToAssessmentsList')}
                      </Button>
                    </VStack>
                  )}

                  {/* Stop Assessment Confirmation Dialog */}
                  <DialogRoot open={showStopConfirm} onOpenChange={(e) => setShowStopConfirm(e.open)} placement="center" scrollBehavior="inside">
                    <DialogBackdrop />
                    <DialogContent>
                      <DialogHeader>{t('createReview.stopAssessmentTitle')}</DialogHeader>
                      <DialogBody>
                        <Text>{t('createReview.stopAssessmentConfirmText', { completed, total })}</Text>
                      </DialogBody>
                      <DialogFooter gap={3}>
                        <Button variant="outline" onClick={() => setShowStopConfirm(false)}>
                          {t('createReview.continueAssessment')}
                        </Button>
                        <Button
                          colorScheme="red"
                          onClick={handleStopAssessment}
                          loading={isStopping}
                        >
                          {t('createReview.confirmStop')}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </DialogRoot>
                </>
              )}
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      {/* Loading Modal */}
      <DialogRoot open={showLoadingModal} onOpenChange={() => {}} placement="center" scrollBehavior="inside" closeOnInteractOutside={false}>
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>{t('createReview.startingYourAssessment')}</DialogHeader>
          <DialogBody>
            <VStack gap={4} align="center">
              <Spinner size="xl" color="brand.500" />
              <Text>{t('createReview.startAssessmentWait')}</Text>
            </VStack>
          </DialogBody>
        </DialogContent>
      </DialogRoot>
      {/* Success Modal */}
      <DialogRoot open={showSuccessModal} onOpenChange={() => {}} placement="center" scrollBehavior="inside" closeOnInteractOutside={false}>
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>{t('createReview.assessmentStarted')}</DialogHeader>
          <DialogBody>
            <Text>{t('createReview.reviewStartedRedirect')}</Text>
          </DialogBody>
        </DialogContent>
      </DialogRoot>
      {/* Error Modal */}
      <DialogRoot open={showErrorModal} onOpenChange={(e) => setShowErrorModal(e.open)} placement="center" scrollBehavior="inside">
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>{t('createReview.error')}</DialogHeader>
          <DialogBody>
            <Text color="red.500">{errorMessage}</Text>
          </DialogBody>
          <DialogFooter>
            <Button colorPalette="red" onClick={() => setShowErrorModal(false)}>
              {t('createReview.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
      <Container maxW="7xl">
        {/* Header */}
        <VStack gap={6} align="stretch">
          <Flex justify="space-between" align="center">
            <VStack align="start" gap={2}>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {t('createReview.newComplianceAssessment')}
              </Text>
              <Text color="gray.600">
                {t('createReview.followStepsCreateGapAssessment')}
              </Text>
            </VStack>
            <Button
              variant="outline"
              colorScheme="red"
              onClick={handleCancel}
            >
              {t('createReview.cancel')}
            </Button>
          </Flex>

          {/* Stepper */}
          <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
            <HStack gap={4} justify="space-between">
              {steps.map((step, index) => {
                const status = getStepStatus(index);
                const isCompleted = status === 'completed';
                const isCurrent = status === 'current';
                const isValid = stepValidation[index];

                return (
                  <HStack
                    key={step.id}
                    gap={3}
                    cursor={index <= currentStep || isValid ? 'pointer' : 'not-allowed'}
                    onClick={() => handleStepClick(index)}
                    opacity={index <= currentStep || isValid ? 1 : 0.5}
                    transition="all 0.2s"
                    _hover={index <= currentStep || isValid ? { opacity: 0.8 } : {}}
                  >
                    {/* Step Circle */}
                    <Box
                      w={10}
                      h={10}
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="lg"
                      fontWeight="bold"
                      bg={
                        isCompleted
                          ? 'green.500'
                          : isCurrent
                          ? 'brand.500'
                          : 'gray.200'
                      }
                      color={
                        isCompleted || isCurrent ? 'white' : 'gray.500'
                      }
                      position="relative"
                    >
                      {isCompleted ? '✓' : step.icon}
                      {isCurrent && (
                        <Box
                          position="absolute"
                          top="-2px"
                          right="-2px"
                          w={4}
                          h={4}
                          borderRadius="full"
                          bg="brand.500"
                          border="2px solid white"
                        />
                      )}
                    </Box>

                    {/* Step Info */}
                    <VStack align="start" gap={1} flex={1}>
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color={
                          isCompleted
                            ? 'green.600'
                            : isCurrent
                            ? 'brand.600'
                            : 'gray.500'
                        }
                      >
                        {step.title}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {step.description}
                      </Text>
                    </VStack>

                    {/* Connector */}
                    {index < steps.length - 1 && (
                      <Box
                        flex={1}
                        h="2px"
                        bg={isCompleted ? 'green.200' : 'gray.200'}
                        mx={2}
                      />
                    )}
                  </HStack>
                );
              })}
            </HStack>
          </Box>

          {/* Step Content */}
          <Box bg="white" p={8} borderRadius="xl" boxShadow="sm" minH="500px">
            {renderStepContent()}
          </Box>

          {/* Navigation */}
          <Flex justify="space-between" align="center">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0 || isSubmitting || showLoadingModal}
            >
              {isRtl ? <FiChevronRight /> : <FiChevronLeft />} {t('createReview.previous')}
            </Button>

            <HStack gap={4}>
              <Badge
                colorScheme="brand"
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
              >
                {t('createReview.stepOfTotal', { current: currentStep + 1, total: steps.length })}
              </Badge>
              {currentStep < steps.length - 1 ? (
                <Button
                  colorScheme="brand"
                  onClick={handleNext}
                  disabled={!validateStep(currentStep) || isSubmitting || showLoadingModal}
                >
                  {t('createReview.next')} {isRtl ? <FiChevronLeft /> : <FiChevronRight />}
                </Button>
              ) : (
                <Button
                  colorScheme="green"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  loadingText={t('createReview.creatingAssessment')}
                  disabled={!reviewData.reviewName.trim() || isSubmitting || showLoadingModal}
                >
                  {t('createReview.startAssessment')} <FiCheck />
                </Button>
              )}
            </HStack>
          </Flex>
        </VStack>
      </Container>

      {/* Cancel Confirmation Dialog */}
      <DialogRoot open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} placement="center" scrollBehavior="inside">
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader fontSize="lg" fontWeight="bold">
            {t('createReview.cancelAssessmentCreation')}
          </DialogHeader>

          <DialogBody>
            {t('createReview.cancelProgressLost')}
          </DialogBody>

          <DialogFooter>
            <Button ref={cancelRef} onClick={() => setIsOpen(false)}>
              {t('createReview.continueEditing')}
            </Button>
            <Button colorPalette="red" onClick={confirmCancel} ml={3}>
              {t('createReview.cancelReview')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  );
};

export default CreateReviewPage; 
